
import { Component, Vue } from 'vue-property-decorator';
import { Action, Getter } from 'vuex-class';
import { DateTime } from 'luxon';
import { decodeMenus } from '@/utils/decoding';
import Toast from '@/utils/toast';
import DuplicateMenusModal from './rotational-menu/DuplicateMenusModal.vue';
import FullPageSpinner from '@/components/shared/suites/FullPageSpinner.vue';
import RotationalMenuCard from './rotational-menu/RotationalMenuCard.vue';
import RotationalMenuItemModal from './rotational-menu/RotationalMenuItemModal.vue';
import TextButton from '../shared/suites/TextButton.vue';

const namespace: string = 'menus';

@Component<RotationalMenus>({
	components: {
		DuplicateMenusModal,
		FullPageSpinner,
		RotationalMenuCard,
		RotationalMenuItemModal,
		TextButton
	}
})
export default class RotationalMenus extends Vue {
	@Action('fetchRotationalMenus', { namespace }) private fetchRotationalMenus!: (payload: { startDate: string; endDate: string }) => void;
	@Action('cleanupRotationalMenus', { namespace }) private cleanupRotationalMenus!: () => Promise<void>;
	@Action('updateRestaurant', { namespace: 'auth' }) private updateRestaurant!: (payload: { restaurant: Restaurant }) => void;
	@Getter('getRotationalMenus', { namespace }) private rotationalMenus!: RotationalMenu[];
	@Getter('getTimezone', { namespace: 'auth' }) private timezone!: string;
	private restaurant: Restaurant | null = null;
	private dailyMenus: DailyMenu[] = [];
	private selectedRotationalMenu: DailyMenu | null = null;
	private weekNumber: number | null = null;
	private year: number | null = null;
	private dateRange: string[] = [];
	private pickerMonth: string = ''; // this date determines which month is displayed in the date picker
	private locale: string = this.$i18n.locale;
	private fetchingMenus: boolean = false;
	private showDuplicateModal: boolean = false;
	private datepickerOpen: boolean = false;
	private showEntireWeek: boolean = false;

	private get dateRangeString(): string {
		const rangeStart: string = DateTime.fromISO(this.dateRange[0]).setLocale(this.locale).toFormat(this.$t('menu.editor.availability.rotational_menus.range_start_format'));
		const rangeEnd: string = DateTime.fromISO(this.dateRange[1]).setLocale(this.locale).toFormat('DD');
		return `${rangeStart} - ${rangeEnd}`;
	}

	private async created(): Promise<void> {
		this.fetchingMenus = true;
		try {
			// Assign the restaurant object directly from the store
			// This is done to avoid reactivity issues when the restaurant object is updated
			// This would cause the component to refetch the rotational when toggling the weekend ordering
			this.restaurant = this.$store.state['auth'].restaurant;
			this.showEntireWeek = this.restaurant?.order_options?.k12_weekend_ordering || false;

			// If the week_start query param is present, use that date to set current week
			const currentDate: DateTime = this.$route.query.week_start
				? DateTime.fromISO(this.$route.query.week_start as string)
				: DateTime.local().setZone(this.timezone);

			this.updateDateInfo(currentDate);
			await this.cleanupRotationalMenus();
			await this.fetchAndSetRotationalMenus();
		}
		catch (errorMessage) {
			Toast.error(this.$t('menu.editor.availability.rotational_menus.error_fetching_menus', { errorMessage }));
		}
		finally {
			this.fetchingMenus = false;
		}
	}

	/**
	 * Update the week number, year, date range and query string based on the current date
	 *
	 * @param {DateTime} currentDate
	 * @return {void}
	 */
	private updateDateInfo(currentDate: DateTime): void {
		this.weekNumber = currentDate.weekNumber;
		this.year = currentDate.weekYear;
		this.dateRange = [currentDate.startOf('week').toISODate(), currentDate.endOf('week').toISODate()];
		this.pickerMonth = currentDate.toISODate().slice(0, -3);

		// Update the week_start query param with the start date of the current date range
		const url = new URL(window.location.href);
		url.searchParams.set('week_start', this.dateRange[0]);
		window.history.replaceState({}, '', url.toString());
	}

	/**
	 * Update the date range and fetch the rotational menus for the updated date range
	 * Is a public method so it can be called from the Availabilities component to re-fetch after setting closed dates
	 *
	 * @return {Promise<void>}
	 */
	public async fetchAndSetRotationalMenus(): Promise<void> {
		this.fetchingMenus = true;
		try {
			await this.fetchRotationalMenus({ startDate: this.dateRange[0], endDate: this.dateRange[1] });
			this.setDailyMenus();
		}
		catch (errorMessage) {
			Toast.error(this.$t('menu.editor.availability.rotational_menus.error_fetching_menus', { errorMessage }));
		}
		finally {
			this.fetchingMenus = false;
		}
	}

	/**
	 * Set the dates for the current week/year and add necessary properties for display
	 *
	 * @return {void}
	 */
	private setDailyMenus(): void {
		if (!this.weekNumber || !this.year) {
			return;
		}
		const firstDayOfWeek = DateTime.fromObject({ weekNumber: this.weekNumber, weekYear: this.year, weekday: 1 });
		this.dailyMenus = Array(7).fill(0).map((_, i) => {
			const date: DateTime = firstDayOfWeek.plus({ days: i });
			const existingMenu: RotationalMenu | undefined = this.rotationalMenus.find((menu: RotationalMenu) => menu.date === date.toISODate());
			const pastDate: boolean = date.startOf('day') < DateTime.local().setZone(this.timezone).startOf('day');
			// We get the restaurant object directly from the store due to reactivity issues mentioned in the created hook
			const closed : boolean = this.$store.state['auth'].restaurant?.holiday_hours?.find((holiday: HolidayHours) => holiday.date === date.toISODate())?.full_day || false;

			return {
				id: existingMenu?.id || undefined,
				date: date.toISODate(),
				formattedDate: date.setLocale(this.locale).toFormat('DD'),
				day: date.setLocale(this.locale).toFormat('cccc'),
				menus: decodeMenus(existingMenu?.menus || [], true),
				pastDate,
				closed
			};
		});
	};

	/**
	 * Change the date range to the next or previous week and fetch the rotational menus
	 *
	 * @return {Promise<void>}
	 */
	private async changeDateRange(direction: 'prev' | 'next'): Promise<void> {
		if (!this.weekNumber || !this.year) {
			return;
		}
		const weeksInYear = DateTime.fromObject({ weekYear: this.year }).weeksInWeekYear;
		this.weekNumber += direction === 'prev' ? -1 : 1;

		// Handle year rollover
		if (this.weekNumber < 1) {
			this.year--;
			this.weekNumber = DateTime.fromObject({ weekYear: this.year }).weeksInWeekYear;
		}
		else if (this.weekNumber > weeksInYear) {
			this.year++;
			this.weekNumber = 1;
		}

		this.updateDateInfo(DateTime.fromObject({ weekNumber: this.weekNumber, weekYear: this.year }));
		await this.fetchAndSetRotationalMenus();
	}

	/**
	 * Re-map the rotational menus to the days after a menu has been added/edited and close the modal
	 *
	 * @return {Promise<void>}
	 */
	private async remapMenusAndCloseModal(): Promise<void> {
		this.setDailyMenus();
		this.selectedRotationalMenu = null;
	}

	/**
	 * Update the weekend ordering setting on the restaurant's menu manager config
	 *
	 * @return {Promise<void>}
	 */
	private async handleWeekendOrderingChange(): Promise<void> {
		if (this.restaurant) {
			try {
				await this.updateRestaurant({ restaurant: { ...this.restaurant, order_options: { ...this.restaurant.order_options, k12_weekend_ordering: this.showEntireWeek } } });
			}
			catch (errorMessage) {
				this.showEntireWeek = !this.showEntireWeek;
				Toast.error(this.$t('menu.editor.availability.rotational_menus.error_toggle_weekend_ordering',
					{
						errorMessage,
						action: this.showEntireWeek ? this.$t('menu.editor.availability.rotational_menus.enable') : this.$t('menu.editor.availability.rotational_menus.disable')
					}));
			}
		}
	}

	/**
	 * Handles the datepicker input event
	 * Set the date range as the first and last days of the week for the selected date, then fetch the rotational menus
	 *
	 * @param {string[]} dateRange
	 * @return {Promise<void>}
	 */
	private async handleDateSelect(dateRange: string[]): Promise<void> {
		const selectedDate = DateTime.fromISO(dateRange[0]);
		this.updateDateInfo(selectedDate);
		await this.fetchAndSetRotationalMenus();
	}

	/**
	 * Set the date range to today and fetch the rotational menus
	 *
	 * @return {Promise<void>}
	 */
	private async setDateToToday(): Promise<void> {
		const currentDate = DateTime.local().setZone(this.timezone);
		this.updateDateInfo(currentDate);
		this.datepickerOpen = false;
		await this.fetchAndSetRotationalMenus();
	}

	private datepickerTitleFormat(date: string | string[]): string {
		if (Array.isArray(date)) {
			return this.$t('menu.editor.availability.rotational_menus.duplicate_date', { date: DateTime.fromISO(date[0]).setLocale(this.locale).toFormat(this.$t('menu.editor.availability.rotational_menus.duplicate_date_format')) });
		}
		return DateTime.fromISO(date).setLocale(this.locale).toFormat('MMMM yyyy');
	}
}
